import React, { useState, useEffect, useRef } from "react";
import Navbar from "./Components/Navbar";
import Footer from "./Components/Footer";
import { useLanguage } from "../../contexts/LanguageContext";

const useInView = (ref, threshold = 0.15) => {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) setInView(true);
      },
      { threshold },
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref, threshold]);
  return inView;
};

const boardMembers = [
  {
    name: "Mustafa Abubakar",
    role: {
      EN: "Minister of State-Owned Enterprises (2009–2011)",
      ID: "Menteri BUMN (2009–2011)",
    },
  },
  {
    name: "Adron Yusuf",
    role: {
      EN: "Acehnese Diaspora in the United States",
      ID: "Diaspora Aceh di Amerika Serikat",
    },
  },
  {
    name: "Hardianto Zainal",
    role: {
      EN: "Acehnese Entrepreneur",
      ID: "Pengusaha Aceh",
    },
  },
  {
    name: "Cut Soraya",
    role: {
      EN: "Vice President of Solusi Bangun Andalas",
      ID: "Vice President Solusi Bangun Andalas",
    },
  },
  {
    name: "Yarmen Dinamika",
    role: {
      EN: "Senior Journalist, Serambi Indonesia",
      ID: "Wartawan Senior Serambi Indonesia",
    },
  },
  {
    name: "Amri Fathmi",
    role: {
      EN: "Religious Figure",
      ID: "Tokoh Agama",
    },
  },
  {
    name: "Siti Rahmah",
    role: {
      EN: "Lecturer of Law",
      ID: "Dosen Ilmu Hukum",
    },
  },
];

const OurBoardSection = () => {
  const { language } = useLanguage();
  const ref = useRef(null);
  const inView = useInView(ref);

  const t = {
    EN: {
      tag: "Our People",
      title: "Our\nBoard",
      subtitle:
        "Rumoh Pangan Aceh is guided by a board of trusted figures from government, business, media, academia, and religious communities, committed to strengthening food security in Aceh.",
    },
    ID: {
      tag: "Orang-Orang Kami",
      title: "Dewan\nKami",
      subtitle:
        "Rumoh Pangan Aceh didampingi oleh dewan yang terdiri dari tokoh-tokoh terpercaya dari pemerintahan, dunia usaha, media, akademisi, dan tokoh agama, yang berkomitmen memperkuat ketahanan pangan di Aceh.",
    },
  };

  const tx = t[language];

  return (
    <section ref={ref} className="bg-white py-24 px-4 relative overflow-hidden">
      <div className="absolute bottom-0 right-0 font-serif text-[130px] font-bold text-[#f0ece3] leading-none select-none pointer-events-none opacity-60 translate-y-4">
        OUR BOARD
      </div>

      <div className="container max-w-6xl mx-auto relative z-10">

        {/* Header */}
        <div
          className="flex flex-col md:flex-row md:items-end md:justify-between mb-14 gap-6"
          style={{
            opacity: inView ? 1 : 0,
            transform: inView ? "translateY(0)" : "translateY(24px)",
            transition: "all 0.8s ease",
          }}
        >
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="h-px w-10 bg-[#c9862a]" />
              <span className="text-[#c9862a] text-xs font-bold tracking-[4px] uppercase">
                {tx.tag}
              </span>
            </div>
            <h2 className="font-serif text-4xl md:text-6xl font-bold text-[#1a1a18] leading-tight">
              {tx.title.split("\n").map((line, i) => (
                <React.Fragment key={i}>
                  {line}
                  {i === 0 && <br />}
                </React.Fragment>
              ))}
            </h2>
          </div>
          <p className="text-[#6b6456] text-base leading-relaxed max-w-sm md:text-right">
            {tx.subtitle}
          </p>
        </div>

        {/* Divider */}
        <div className="flex items-center gap-4 mb-14">
          <div className="flex-1 h-px bg-[#e8e0d0]" />
          <div className="w-2 h-2 bg-[#c9862a] rotate-45" />
          <div className="flex-1 h-px bg-[#e8e0d0]" />
        </div>

        {/* Board Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {boardMembers.map(({ name, role }, i) => (
            <div
              key={name}
              className="group border border-[#e8e0d0] p-8 flex flex-col hover:border-[#c9862a]/50 hover:shadow-lg transition-all duration-300 relative overflow-hidden bg-white"
              style={{
                opacity: inView ? 1 : 0,
                transform: inView ? "translateY(0)" : "translateY(32px)",
                transition: `all 0.7s ease ${0.15 + i * 0.08}s`,
              }}
            >

              {/* Name */}
              <h3 className="font-serif text-xl font-bold text-[#1a1a18] mb-2 leading-snug">
                {name}
              </h3>

              {/* Gold divider */}
              <div className="w-8 h-[2px] bg-[#c9862a] mb-4 transition-all duration-500 group-hover:w-16" />

              {/* Role */}
              <p className="text-[#6b6456] text-sm leading-relaxed">
                {role[language]}
              </p>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
};

// ─── Page ──────────────────────────────────────────────────────────────────────
const OurBoardPage = () => (
  <main className="overflow-x-hidden font-homepage bg-white">
    <Navbar />
    <OurBoardSection />
    <Footer />
  </main>
);

export default OurBoardPage;